import React, { useContext } from 'react';
import {
  Box,
  Typography,
  Grid,
  Chip,
  Paper,
  Button,
  LinearProgress,
  Divider
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import { AuthContext } from '../../contexts/AuthContext';
import { 
  getTargetTypeLabel, 
  getStatusLabel, 
  getPeriodLabel,
  getStatusColor,
  getProgressColor
} from '../../constants/goalsConstants';

const GoalDetails = ({ goal, onEdit, onDelete }) => {
  const { isAdmin } = useContext(AuthContext);
  
  // Oblicz procent realizacji
  const progressPercentage = goal.targetValue > 0 
    ? Math.min(Math.round((goal.currentProgress / goal.targetValue) * 100), 100)
    : 0;
  
  const formatDate = (date) => {
    if (!date) return 'Brak daty';
    return format(date instanceof Date ? date : date.toDate(), 'dd.MM.yyyy', { locale: pl });
  };

  return (
    <Box>
      {/* Nagłówek z podstawowymi informacjami */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Szczegóły celu</Typography>
        {isAdmin && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<EditIcon />}
              onClick={onEdit}
            >
              Edytuj
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={onDelete}
            >
              Usuń
            </Button>
          </Box>
        )}
      </Box>

      {/* Główne informacje o celu */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                {getPeriodLabel(goal.year, goal.month, goal.quarter)} - {getTargetTypeLabel(goal.targetType)}
              </Typography>
              <Chip 
                label={getStatusLabel(goal.status)} 
                color={getStatusColor(goal.status)}
              />
            </Box>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">Postęp</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <Box sx={{ width: '100%', mr: 1 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={progressPercentage} 
                      color={getProgressColor(goal.currentProgress, goal.targetValue)}
                      sx={{ height: 10, borderRadius: 5 }}
                    />
                  </Box>
                  <Box sx={{ minWidth: 35 }}>
                    <Typography variant="body2" color="textSecondary">{`${progressPercentage}%`}</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="body2">
                    Aktualnie: {goal.currentProgress} {goal.targetType === 'sales_value' ? 'PLN' : ''}
                  </Typography>
                  <Typography variant="body2">
                    Cel: {goal.targetValue} {goal.targetType === 'sales_value' ? 'PLN' : ''}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            
            <Divider sx={{ my: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">Okres</Typography>
                <Typography variant="body1">
                  {formatDate(goal.startDate)} - {formatDate(goal.endDate)}
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">Przypisane do</Typography>
                <Typography variant="body1">
                  {goal.assignedTo ? goal.assignedToName || goal.assignedTo : 'Cała firma'}
                </Typography>
              </Grid>
              
              {goal.notes && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">Notatki</Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {goal.notes}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Informacje dodatkowe
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">Data utworzenia</Typography>
                <Typography variant="body1">
                  {formatDate(goal.createdAt)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">Ostatnia aktualizacja</Typography>
                <Typography variant="body1">
                  {formatDate(goal.lastUpdate)}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default GoalDetails;
